import {
  PropsWithChildren,
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useState,
  useEffect,
} from "react";
import ky from "ky";
import { User } from "types/qbank/user";
import { OAuthToken } from "types/qbank/token";
import { useAuth } from "./auth";
import { ConfigV2, ContentCategory } from "types/appConfigV2";
import { SearchModel } from "types/qbank/searchModel";
import { ResultSet } from "types/qbank/resultSet";
import { Media } from "types/qbank/media";
import { Folder } from "types/qbank/folder";
import { MetaData } from "types/qbank/metadata";
import getWindowTab from "utilities/getWindowTab";
import { EventSession } from "types/qbank/eventSession";
import { SessionDetails } from "./sessionDetails";
import { useQueryClient } from "react-query";
import { DeploymentSite } from "types/qbank/deploymentSite";
import { DeployedFile } from "types/qbank/deployedFile";
import { PropertyType } from 'types/qbank/propertyType';
import { MoodBoard } from "types/qbank/moodboard";

type ApiConfig = {
  host: string;
  client_id: string;
};

type LoginVariables = {
  host: string;
  username: string;
  password: string;
};

type LoginWithClientIdVariables = {
  host: string;
  username: string;
  password: string;
  clientId: string;
};

type LoginWithAuthCodeVariables = {
  host: string;
  clientId: string;
  code: string;
  redirect_uri: string;
};

type RefreshTokenVariables = {
  refresh_token: string;
};

type LogUsageVariables = {
  sessionId: number;
  sessionDetails: SessionDetails;
  media: Media;
};

type EventSessionVariables = {
  eventSession: EventSession;
};

type IProps = PropsWithChildren<{}>;

const ApiContext = createContext<{
  api: ApiConfig;
  setApi: Dispatch<SetStateAction<ApiConfig>>;
  login: (args0: LoginVariables) => Promise<OAuthToken>;
  loginWithClientId: (args0: LoginWithClientIdVariables) => Promise<OAuthToken>;
  loginWithCode: (args0: LoginWithAuthCodeVariables) => Promise<OAuthToken>;
  refreshToken: (args0: RefreshTokenVariables) => Promise<OAuthToken>;
  getCurrentUser: () => Promise<User>;
  getRemoteOverride: () => Promise<ConfigV2>;
  getMetaData: () => Promise<MetaData>;
  search: (args0: SearchModel) => Promise<ResultSet>;
  getMedia: (mediaId: number) => Promise<Media>;
  getUser: (userId: number) => Promise<User>;
  getFolder: (folderId: number) => Promise<Folder>;
  getDeploymentSite: (deploymentSiteId: number) => Promise<DeploymentSite>;
  getMediaDeployedFiles: (mediaId: number) => Promise<DeployedFile[]>;
  logUsage: (args0: LogUsageVariables) => Promise<any>;
  session: (args0: EventSessionVariables) => Promise<any>;
  getPropertSet: () => Promise<PropertyType[]>;
  getFolders: (folderId: number) => Promise<Folder[]>;
  getmoodboards: () => Promise<MoodBoard[]>;
}>(undefined!);

export const ApiProvider = ({ children }: IProps) => {
  const queryClient = useQueryClient();
  const host = localStorage.getItem("host");

  const { setAuth } = useAuth();
  const [api, setApi] = useState<ApiConfig>({
    host: host || process.env.REACT_APP_host || "",
    client_id: process.env.REACT_APP_client_id || "",
  });
  const token = JSON.parse(localStorage.getItem("token") || "{}") as OAuthToken;

  let apiClient = ky.create({
    prefixUrl: `https://${api.host}/`,
    hooks: {
      beforeRequest: [
        (request) => {
          if (token && token.access_token && token.token_type === "Bearer") {
            request.headers.set(
              "Authorization",
              `Bearer ${token.access_token}`
            );
          }
        },
      ],
      afterResponse: [
        async (request, options, response) => {
          if (response.status === 401) {
            if (token && token.refresh_token && token.refresh_token !== "") {
              const newToken = await refreshToken({
                refresh_token: token.refresh_token,
              });
              request.headers.set(
                "Authorization",
                `Bearer ${newToken.access_token}`
              );
              queryClient.clear();
              setAuth({ isLoggedIn: true, oAuthToken: newToken });
            } else {
              setAuth({ isLoggedIn: false });
            }
          }
        },
      ],
    },
  });

  useEffect(() => {
    apiClient.extend({
      prefixUrl: `https://${api.host}/`,
    });
  }, [apiClient, api.host]);

  const login = ({
    host,
    username,
    password,
  }: LoginVariables): Promise<OAuthToken> => {
    setApi({ ...api, host });
    return apiClient
      .post("api/v1/oauth2/token", {
        json: {
          username,
          password,
          client_id: api.client_id,
          grant_type: "password",
        },
        prefixUrl: `https://${host}/`,
      })
      .json();
  };

  const loginWithClientId = ({
    host,
    username,
    password,
    clientId,
  }: LoginWithClientIdVariables): Promise<OAuthToken> => {
    setApi({ ...api, host });
    return apiClient
      .post("api/v1/oauth2/token", {
        json: {
          username,
          password,
          client_id: clientId,
          grant_type: "password",
        },
        prefixUrl: `https://${host}/`,
      })
      .json();
  };

  const loginWithCode = ({
    host,
    clientId,
    code,
    redirect_uri
  }: LoginWithAuthCodeVariables): Promise<OAuthToken> => {
    setApi({ ...api, host });
    return apiClient
      .post("api/v1/oauth2/token", {
        json: {
          redirect_uri: redirect_uri,
          client_id: clientId,
          code: code,
          grant_type: "authorization_code",
        },
        prefixUrl: `https://${host}/`,
      })
      .json();
  };

  const refreshToken = ({
    refresh_token,
  }: RefreshTokenVariables): Promise<OAuthToken> => {
    return apiClient
      .post("api/v1/oauth2/token", {
        json: {
          grant_type: "refresh_token",
          client_id: api.client_id,
          refresh_token: refresh_token,
        },
      })
      .json();
  };

  const getCurrentUser = (): Promise<User> => {
    return apiClient.get("api/v1/accounts/me.json").json();
  };


  const getRemoteOverride = (): Promise<ConfigV2> => {
    return apiClient
      .get("api/v1/connectors.json/browser", {
        parseJson: (response) => {
          let remoteOverride: ConfigV2 = {
            categories: {},
            targets: {},
            sourceId: 1,
            config: {
              folders: true,
              moodboard: true
            },
          };
          if (typeof response === "string") {
            remoteOverride = JSON.parse(response);
          }
          const categoryList: Record<string, ContentCategory> = {};
          Object.entries(remoteOverride.targets).forEach(([key, element]) => {
            categoryList[key] = {
              filter: element.mediaFilter,
              propertyRequests: element.properties,
              id: element.id,
              title: element.title,
              icon: element.icon,
              description: element.description,
              rootFolderId: element.rootFolderId !== null && element.rootFolderId !== undefined ? element.rootFolderId : 0,
              deploymentSiteId: element.deploymentSiteId,
              imageTemplateId: element.imageTemplateId,
            };
          });
          remoteOverride.categories = categoryList;

          if (remoteOverride.categories) {
            for (const categoryId in remoteOverride.categories) {
              if (
                !remoteOverride.categories[categoryId].propertyRequests ||
                typeof remoteOverride.categories[categoryId]
                  .propertyRequests !== "object"
              ) {
                remoteOverride.categories[categoryId].propertyRequests = [];
              }

              if (
                typeof remoteOverride.categories[categoryId].rootFolderId ===
                "string"
              ) {
                remoteOverride.categories[categoryId].rootFolderId = parseInt(
                  JSON.parse(
                    //@ts-ignore API response might be empty string
                    remoteOverride.categories[categoryId].rootFolderId === ""
                      ? '{"folderid": "0", "name": "root"}'
                      : remoteOverride.categories[categoryId].rootFolderId
                  ).folderid
                );
              }
            }
          }
          return remoteOverride;
        },
      })
      .json();
  };

  const getMedia = (mediaId: number): Promise<Media> => {
    return apiClient.get(`api/v1/media.json/${mediaId}?includeChildren=true`).json();
  };

  const getUser = (userId: number): Promise<User> => {
    return apiClient.get(`api/v1/accounts/users.json/${userId}`).json();
  };

  const getMetaData = (): Promise<MetaData> => {
    return apiClient.get("api/v1/search/metadata.json").json();
  };

  const getFolder = (folderId: number): Promise<Folder> => {
    const url =
      folderId === 0
        ? "api/v1/folders.json?depth=2"
        : `api/v1/folders.json/${folderId}?depth=2`;
    return apiClient
      .get(url, {
        hooks: {
          afterResponse: [
            async (request, options, response) => {
              if (!response.ok) {
                throw Error(`Failed to fetch folder`);
              }
            },
          ],
        },
        parseJson: (response) => {
          let folder = JSON.parse(response);
          if (folder.error) {
            throw Error("Failed to fetch folder");
          } else if (folder.deleted) {
            throw Error(
              "Oops! We couldn’t find the folder. Please contact QBank Support"
            );
          } else if (folderId === 0) {
            //Simulate folder representation for the root
            folder = {
              name: "Home",
              id: 0,
              subFolders: folder,
            };
          } else if (!folder.subFolders) {
            folder.subFolders = [];
          }
          return folder;
        },
      })
      .json();
  };

  const search = (searchParams: SearchModel): Promise<ResultSet> => {
    return apiClient.post("api/v1/search.json", { json: searchParams }).json();
  };

  const getDeploymentSite = (
    deploymentSiteId: number
  ): Promise<DeploymentSite> => {
    return apiClient
      .get(`api/v1/deployment/sites.json/${deploymentSiteId}`)
      .json();
  };

  const getMediaDeployedFiles = (mediaId: number): Promise<DeployedFile[]> => {
    return apiClient
      .get(`api/v1/media.json/${mediaId}/deployment/files`)
      .json();
  };

  const logUsage = async ({
    sessionId,
    sessionDetails,
    media,
  }: LogUsageVariables): Promise<any> => {
    const windowTab = await getWindowTab();

    return apiClient
      .post("api/v1/events/usage", {
        json: {
          sessionId: sessionId,
          mediaUsage: {
            mediaId: media.mediaId,
            mediaUrl: windowTab.url ?? "n/a",
            pageUrl: windowTab.url ?? "Unknown Connector v2 host",
            language: "eng",
            context: {
              createdByName: sessionDetails?.username,
              createdByEmail: sessionDetails?.email,
            },
          },
        },
      })
      .json();
  };

  const session = ({
    eventSession,
  }: EventSessionVariables): Promise<number> => {
    return apiClient
      .post("api/v1/events/session", {
        json: eventSession,
      })
      .json();
  };

  const getPropertSet = (): Promise<PropertyType[]> => {
    return apiClient.get("api/v1/propertysets/propertytypes.json").json();
  };

  const getFolders = (folderId: number): Promise<Folder[]> => {
    const url =
      folderId === 0
        ? "api/v1/folders.json?depth=10"
        : `api/v1/folders.json/${folderId}?depth=10`;
    return apiClient
      .get(url, {
        hooks: {
          afterResponse: [
            async (request, options, response) => {
              if (!response.ok) {
                throw Error(`Failed to fetch folders`);
              }
            },
          ],
        },
        parseJson: (response) => {
          let folders = JSON.parse(response);
          if (folders.error) {
            throw Error("Failed to fetch folders");
          }
          return folders;
        },
      })
      .json();
  };

  const getmoodboards = (): Promise<MoodBoard[]> => {
    const url = "api/v1/moodboards.json";
    return apiClient
      .get(url, {
        hooks: {
          afterResponse: [
            async (request, options, response) => {
              if (!response.ok) {
                throw Error(`Failed to fetch moodboards`);
              }
            },
          ],
        },
        parseJson: (response) => {
          let moodboards = JSON.parse(response);
          if (moodboards.error) {
            throw Error("Failed to fetch moodboards");
          }
          return moodboards;
        },
      })
      .json();
  };

  return (
    <ApiContext.Provider
      value={{
        api,
        setApi,
        login,
        loginWithClientId,
        loginWithCode,
        refreshToken,
        getCurrentUser,
        getRemoteOverride,
        getMetaData,
        search,
        getMedia,
        getUser,
        getFolder,
        getDeploymentSite,
        getMediaDeployedFiles,
        logUsage,
        session,
        getPropertSet,
        getFolders,
        getmoodboards,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};

export const useApi = () => {
  let context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error("useApi must be used within a ApiProvider");
  }
  return context;
};
